import { apiService } from './api';
import { Payslip } from '@/types';
import { toast } from 'sonner';

export interface EmailData {
  email: string;
  subject?: string;
  message?: string;
}

/** Normalize API payslip row (period or employee endpoints) to frontend Payslip shape. */
function transformPayslipRow(payslip: Record<string, unknown>): Payslip {
  const p = payslip as Record<string, any>;
  return {
    id: p.p_id?.toString() || p.id?.toString() || '',
    employeeId: p.employee_id?.toString() || '',
    period: p.period || '',
    issueDate: p.issue_date || '',
    basicSalary: parseFloat(p.basic_salary) || 0,
    totalAllowances: parseFloat(p.total_allowances) || 0,
    totalDeductions: parseFloat(p.total_deductions) || 0,
    netSalary: parseFloat(p.net_salary) || 0,
    status: p.status || 'draft',
    paymentDate: p.payment_date,
    paymentReference: p.payment_reference || '',
    payPeriod: p.period || '',
    council: p.council,
    basicPay: parseFloat(p.basic_salary) || 0,
    allowanceBreakdown: p.allowance_breakdown || [],
    deductionBreakdown: p.deduction_breakdown || [],
    grossSalary: (parseFloat(p.basic_salary) || 0) + (parseFloat(p.total_allowances) || 0),
    grossPayYTD: p.gross_pay_ytd || 0,
    taxMonthly: p.tax_monthly || 0,
    taxYTD: p.tax_ytd || 0,
    napsaMonthly: p.napsa_monthly || 0,
    lasfMonthly: p.lasf_monthly || 0,
    napsaYTD: p.napsa_ytd || 0,
    lasfYTD: p.lasf_ytd || 0,
    pspfYTD: p.pspf_ytd || 0,
    closingLeaveDays: Number(p.closing_leave_days ?? p.closingLeaveDays ?? 0) || 0,
    closingLeaveDaysValue: Number(p.closing_leave_days_value ?? p.closingLeaveDaysValue ?? 0) || 0,
    openingLeaveDays: Number(p.opening_leave_days ?? p.openingLeaveDays ?? 0) || 0,
    advanceBF: Number(p.advance_bf ?? p.advanceBF ?? 0) || 0,
    advanceCF: Number(p.advance_cf ?? p.advanceCF ?? 0) || 0,
    generatedDate: p.created_at || '',
    paymentMethod: p.payment_method || 'bank',
    notes: p.notes || '',
    createdAt: p.created_at || '',
    updatedAt: p.updated_at || '',
    employeeName: p.firstName && p.lastName ? `${p.firstName} ${p.lastName}` : '',
    employeeEmail: p.email || '',
    employeeIdNumber: p.employeeId || '',
    userId: p.userId || '',
    otherName: p.otherName || '',
    phone: p.phone || '',
    cellNumber: p.cellNumber || '',
    position: p.position || p.position_title || '',
    positionId: p.position_id || 0,
    department: p.department || p.department_name || '',
    departmentId: p.department_id || 0,
    division: p.division || '',
    joinDate: p.joinDate || '',
    engagementDate: p.engagementDate || '',
    dateOfBirth: p.dateOfBirth || '',
    gender: p.gender || '',
    socialSecurityNo: p.socialSecurityNo || '',
    bankName: p.bankName || '',
    bankAccount: p.bankAccount || '',
    bankBranch: p.bankBranch || '',
    swiftcode: p.swiftcode || p.swiftCode || '',
    manNumber: p.manNumber || '',
    nrc: p.nrc || '',
    jobTitle: p.jobTitle || p.position_title || '',
    taxId: p.taxId || '',
    nhimaNumber: p.nhimaNumber || p.nhima_number || '',
    unionType: p.unionType || '',
    contributionType: p.contributionType || '',
  };
}

function unwrapPayslipList(response: unknown): Record<string, unknown>[] {
  if (response && typeof response === 'object' && 'data' in response && Array.isArray((response as { data: unknown }).data)) {
    return (response as { data: Record<string, unknown>[] }).data;
  }
  if (Array.isArray(response)) {
    return response as Record<string, unknown>[];
  }
  return [];
}

export class PayslipService {
  /**
   * Download a single payslip as PDF
   */
  static async downloadPayslip(payslipId: string): Promise<void> {
    try {
      const response = await apiService.downloadPayslip(payslipId);
      
      // Create blob and download
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payslip-${payslipId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Payslip downloaded successfully');
    } catch (error) {
      console.error('Error downloading payslip:', error);
      toast.error('Failed to download payslip');
      throw error;
    }
  }

  /**
   * Print a single payslip
   */
  static async printPayslip(payslipId: string): Promise<void> {
    try {
      const response = await apiService.printPayslip(payslipId);
      
      // Create blob and open in new window for printing
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
          setTimeout(() => {
            printWindow.close();
            window.URL.revokeObjectURL(url);
          }, 1000);
        };
      }
      
      toast.success('Payslip opened for printing');
    } catch (error) {
      console.error('Error printing payslip:', error);
      toast.error('Failed to print payslip');
      throw error;
    }
  }

  /**
   * Email a single payslip
   */
  static async emailPayslip(payslipId: string, emailData: EmailData): Promise<void> {
    try {
      const response = await apiService.emailPayslip(payslipId, emailData);

      // Backend sometimes returns a body with status/message even on HTTP 200
      const bodyStatus = (response?.status ?? 200);
      const isOk = bodyStatus === 200 || bodyStatus === 'success' || bodyStatus === true;
      if (!isOk) {
        const message = response?.message || 'Failed to send payslip via email';
        toast.error(message);
        throw new Error(message);
      }

      toast.success(response?.message || 'Payslip sent via email successfully');
    } catch (error) {
      console.error('Error emailing payslip:', error);
      if (!(error instanceof Error && error.message)) {
        toast.error('Failed to send payslip via email');
      }
      throw error;
    }
  }

  /**
   * Bulk download multiple payslips as a ZIP archive
   */
  static async bulkDownloadPayslips(payslipIds: string[]): Promise<{ filename: string; count: number }> {
    if (!payslipIds.length) {
      throw new Error('No payslips selected for download');
    }

    try {
      const { blob, filename } = await apiService.bulkDownloadPayslips(payslipIds);
      const downloadName = filename || `payslips-${new Date().toISOString().split('T')[0]}.zip`;

      const zipBlob =
        blob.type && blob.type !== 'application/octet-stream'
          ? blob
          : new Blob([blob], { type: 'application/zip' });
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { filename: downloadName, count: payslipIds.length };
    } catch (error) {
      console.error('Error downloading payslips:', error);
      throw error;
    }
  }

  /**
   * Bulk email multiple payslips
   */
  static async bulkEmailPayslips(payslipIds: string[], emailData: EmailData): Promise<void> {
    try {
      await apiService.bulkEmailPayslips(payslipIds, emailData);
      toast.success(`${payslipIds.length} payslips sent via email successfully`);
    } catch (error) {
      console.error('Error emailing payslips:', error);
      toast.error('Failed to send payslips via email');
      throw error;
    }
  }

  /**
   * Get payslip data from backend
   */
  static async getPayslipById(id: string): Promise<Payslip> {
    try {
      const response = await apiService.getPayslipById(id);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching payslip:', error);
      toast.error('Failed to fetch payslip data');
      throw error;
    }
  }

  /**
   * Get all payslips from backend
   */
  static async getAllPayslips(cid?: number | null): Promise<Payslip[]> {
    try {
      const response = await apiService.getAllPayslips(cid);
      
      
      // Handle different response structures
      let payslipsData;
      if (response && response.data) {
        payslipsData = response.data;
      } else if (Array.isArray(response)) {
        payslipsData = response;
      } else {
        console.warn('Unexpected response structure:', response);
        payslipsData = [];
      }
      
      
      return payslipsData;
    } catch (error) {
      console.error('Error fetching payslips:', error);
      toast.error('Failed to fetch payslips data');
      throw error;
    }
  }

  /**
   * Get payslips by employee ID from backend
   */
  static async getPayslipsByEmployee(employeeId: string): Promise<Payslip[]> {
    try {
      const response = await apiService.getPayslipsByEmployee(employeeId);
      
      const payslipsData = unwrapPayslipList(response);
      return payslipsData.map(transformPayslipRow);
    } catch (error) {
      console.error('Error fetching payslips by employee:', error);
      toast.error('Failed to fetch employee payslips data');
      throw error;
    }
  }

  /**
   * Get payslips by period from backend
   */
  static async getPayslipsByPeriod(period: string, cid?: number | null): Promise<Payslip[]> {
    if (cid == null || Number(cid) <= 0) {
      return [];
    }
    try {
      const response = await apiService.getPayslipsByPeriod(period, cid);
      const payslipsData = unwrapPayslipList(response);
      return payslipsData.map(transformPayslipRow);
    } catch (error) {
      console.error('Error fetching payslips by period:', error);
      toast.error('Failed to fetch payslips data for period');
      throw error;
    }
  }

  /**
   * Update payslip status
   */
  static async updatePayslip(id: string, updates: Partial<Payslip>): Promise<Payslip> {
    try {
      const response = await apiService.updatePayslip(id, updates);
      toast.success('Payslip updated successfully');
      return response.data || response;
    } catch (error) {
      console.error('Error updating payslip:', error);
      toast.error('Failed to update payslip');
      throw error;
    }
  }

  /**
   * Create new payslip
   */
  static async createPayslip(payslip: Payslip): Promise<Payslip> {
    try {
      const response = await apiService.createPayslip(payslip);
      toast.success('Payslip created successfully');
      return response.data || response;
    } catch (error) {
      console.error('Error creating payslip:', error);
      toast.error('Failed to create payslip');
      throw error;
    }
  }
} 